import { doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { getMessaging, getToken, isSupported } from 'firebase/messaging'
import { app, db, logFirebaseError } from '../lib/firebase'
import { isStandalone, registerAdminServiceWorker } from '../utils/pwa'
import type { SwDiagnostics } from '../utils/pwa'

/**
 * Offentlig web-push-nøgle (VAPID) fra Firebase Console → Projektindstillinger →
 * Cloud Messaging → Web Push certificates. Nøglen er offentlig og må stå i frontend —
 * den private del ligger kun hos Firebase.
 */
export const FCM_VAPID_KEY = 'BDgjxPCdcRHvBVh5pQydpEr1f-vHv1-DkSOUBK8bUhNHK0VzqDjs5nSersIv0hVPKbrLlSB1q1yMg8lEphNOQzM'

/** Status vist under Indstillinger → Notifikationer. */
export type PushStatus =
  | 'checking'
  | 'active'
  | 'permission-missing'
  | 'permission-denied'
  | 'install-required'
  | 'unsupported'
  | 'registration-failed'

export interface PushCheck {
  status: PushStatus
  /** Teknisk detalje til fejlsøgning, fx Firebase-fejlkode. */
  detail?: string
  /** Midlertidig fejlsøgning af service workeren. */
  diagnostics?: SwDiagnostics
}

export interface PushCapabilities {
  secureContext: boolean
  serviceWorker: boolean
  notifications: boolean
  pushManager: boolean
  standalone: boolean
  /**
   * Platformen har en hjemmeskærms-tilstand (navigator.standalone findes — fx
   * iPhone/iPad). Her er push kun tilgængelig i den installerede app.
   */
  homeScreenPlatform: boolean
}

/** Faktiske platform-egenskaber — ingen user-agent-gæt. */
export function pushCapabilities(): PushCapabilities {
  return {
    secureContext: window.isSecureContext,
    serviceWorker: 'serviceWorker' in navigator,
    notifications: 'Notification' in window,
    pushManager: 'PushManager' in window,
    standalone: isStandalone(),
    homeScreenPlatform: 'standalone' in navigator,
  }
}

/**
 * Afgør om push kan bruges lige nu — eller hvad der mangler.
 * "Understøttes ikke" vises kun, når de nødvendige API'er reelt mangler.
 */
function capabilityStatus(caps: PushCapabilities): PushStatus | null {
  const apisAvailable = caps.serviceWorker && caps.notifications && caps.pushManager

  // Hjemmeskærms-platform i almindelig browser: push åbnes først i den installerede app.
  if (caps.homeScreenPlatform && !caps.standalone) return 'install-required'

  if (!caps.secureContext || !caps.serviceWorker) return 'unsupported'

  if (!apisAvailable) {
    // Service workers findes, men push-API'erne er skjult udenfor installeret tilstand.
    return caps.standalone ? 'unsupported' : 'install-required'
  }
  return null
}

function logCapabilities(caps: PushCapabilities, status: PushStatus) {
  // eslint-disable-next-line no-console
  console.info('[Push] egenskaber', { ...caps, status, permission: caps.notifications ? Notification.permission : 'n/a' })
}

/**
 * Henter FCM-tokenet via den eksisterende Firebase-app og appens eneste service
 * worker (/firebase-messaging-sw.js, scope "/"), og gemmer det under brugeren:
 * adminDevices/{uid}/tokens/{token}. Spørger ALDRIG om tilladelse selv og kaldes
 * først, når bruger er logget ind, siden kører HTTPS og tilladelsen er givet.
 */
async function registerToken(uid: string): Promise<PushCheck> {
  if (!uid) return { status: 'registration-failed', detail: 'Ikke logget ind.' }
  if (Notification.permission !== 'granted') return { status: 'permission-missing' }

  // Firebase' egen kontrol logges kun — den afgør ikke, om vi forsøger.
  const firebaseSupported = await isSupported().catch(() => false)
  if (!firebaseSupported) {
    // eslint-disable-next-line no-console
    console.warn('[Push] firebase/messaging isSupported() = false — forsøger alligevel, da platformens API\'er findes')
  }

  // Service workeren registreres og navigator.serviceWorker.ready afventes FØR getToken.
  const sw = await registerAdminServiceWorker()
  const registration = sw.registration
  if (!registration) {
    return { status: 'registration-failed', detail: sw.error ?? 'Ukendt fejl', diagnostics: sw.diagnostics }
  }
  try {
    const token = await getToken(getMessaging(app), {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: registration,
    })
    if (!token) return { status: 'registration-failed', detail: 'Firebase returnerede intet token.' }
    await setDoc(
      doc(db, 'adminDevices', uid, 'tokens', token),
      { token, device: deviceName(), standalone: isStandalone(), lastSeen: serverTimestamp() },
      { merge: true },
    )
    // eslint-disable-next-line no-console
    console.info('[Push] FCM-token gemt for', uid)
    return { status: 'active' }
  } catch (error) {
    logFirebaseError('registerPushToken', error)
    const code = (error as { code?: string })?.code
    const message = (error as { message?: string })?.message
    return {
      status: 'registration-failed',
      detail: [code, message].filter(Boolean).join(' — ') || 'Ukendt fejl',
      diagnostics: sw.diagnostics,
    }
  }
}

/**
 * Beder om tilladelse og registrerer denne enhed til push.
 * Kaldes KUN når admin selv slår en påmindelse til — aldrig ved åbning af appen.
 */
export async function enablePush(uid: string): Promise<PushCheck> {
  const caps = pushCapabilities()
  const blocked = capabilityStatus(caps)
  logCapabilities(caps, blocked ?? 'checking')
  if (blocked) return { status: blocked }

  const permission = await Notification.requestPermission()
  if (permission === 'denied') return { status: 'permission-denied' }
  if (permission !== 'granted') return { status: 'permission-missing' }
  return registerToken(uid)
}

/**
 * Aktuel status uden at spørge om noget. Er tilladelsen allerede givet,
 * fornyes tokenet stille, så det altid er gyldigt i Firestore.
 */
export async function checkPushStatus(uid: string): Promise<PushCheck> {
  const caps = pushCapabilities()
  const blocked = capabilityStatus(caps)
  logCapabilities(caps, blocked ?? 'checking')
  if (blocked) return { status: blocked }
  if (Notification.permission === 'denied') return { status: 'permission-denied' }
  if (Notification.permission !== 'granted') return { status: 'permission-missing' }
  return registerToken(uid)
}

/** Kun en læsbar etiket til enhedslisten i Firestore — bruges aldrig til at afgøre support. */
function deviceName(): string {
  const agent = navigator.userAgent
  if (/iphone/i.test(agent)) return 'iPhone'
  if (/ipad/i.test(agent)) return 'iPad'
  if (/android/i.test(agent)) return 'Android'
  if (/mac/i.test(agent)) return 'Mac'
  if (/windows/i.test(agent)) return 'Windows'
  return 'Browser'
}
